import { priceTableData } from "constants/prices";

import { FC, useContext, useEffect, useState } from "react";
import "styles/questionnaire/shared.less";

import { Typography } from "antd";
import { AppContext } from "App";
import hmrcBadge from "assets/images/new-questionnaire/hmrc_badge.svg";
import Spinner from "components/basic/Spinner/Spinner";
import { PriceCard } from "components/pages/Prices/components/PriceCard";
import PriceTables, { TableCategory, categoryMap, PricingCategory } from "components/pages/Prices/components/PriceTables";
import { logGoogleAnalyticsEvent } from "utils/analytics";

import { DiscountBox } from "./DiscountBox";

type PriceBreakdownProps = {
    setAmount: (_amount: number) => void;
    amount: number
    isLoadingOrderToken: boolean;
    styles?: {
        isFlex?: boolean,
        width?: string;
        hideBorder?: boolean;
    };
    documentId: string,
    setAccountantReviewRequested: (_value: boolean) => void;
    pricingCategory: string;
};

export const PriceBreakDown: FC<PriceBreakdownProps> = ({ amount, setAmount, isLoadingOrderToken, styles, documentId, setAccountantReviewRequested, pricingCategory }) => {
    const { isMobile } = useContext(AppContext);
    const { width } = styles || {};
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [, setOldAmount] = useState<number | null>(null);

    const categories = categoryMap[pricingCategory as PricingCategory] || [];
    const employmentTypeCard = priceTableData.find(card => categories.includes(card.category as TableCategory));
    const accountantCard = priceTableData.find(
        card => card.category === TableCategory.AccountantReviewedReturn
    );

    const vatRate = 0.2;

    const getOriginalGross = () => {
        if (selectedKey !== TableCategory.AccountantReviewedReturn) {
            return parseFloat(employmentTypeCard?.originalPrice ?? "0");
        } else if (selectedKey === TableCategory.AccountantReviewedReturn) {
            return parseFloat(accountantCard?.originalPrice ?? "0");
        }
        return 0;
    };

    const getDiscountedGross = () => {
        return amount;
    };

    const getDiscount = () => {
        const original = getOriginalGross();
        const discounted = getDiscountedGross();
        const discount = original - discounted;
        return discount.toFixed(2);
    };

    const getNetPriceExcludingVAT = () => {
        const discountedGross = getDiscountedGross();
        const net = discountedGross / (1 + vatRate);
        return net.toFixed(2);
    };

    const getVATFromDiscountedGross = () => {
        const discountedGross = getDiscountedGross();
        const net = discountedGross / (1 + vatRate);
        const vat = discountedGross - net;
        return vat.toFixed(2);
    };

    const getTotalCost = () => {
        return getDiscountedGross().toFixed(2);
    };

    const onPayNowClick = (selectedPrice: number) => {
        setAmount(selectedPrice);
        logGoogleAnalyticsEvent("pay_now_clicked", {
            platform: isMobile ? "mobile" : "desktop",
            amount: amount,
            document_id: documentId,
            screen: "price_breakdown",
        });
    };

    useEffect(() => {
        if (!selectedKey) {
            const defaultTable = priceTableData.find(table => table.category !== TableCategory.AccountantReviewedReturn);
            if (defaultTable) {
                setSelectedKey(defaultTable.category);
            }
        }
    }, [selectedKey]);
    return (
        !isMobile ? (
            <div className={`price-breakdown-outer-div${isMobile ? "-mobile" : "-desktop"} common-font-style`}>
                <div className="why-you-need-to-pay-container" style={{ width }}>
                    <div className="price-breakdown-title" >
                        <span>
                            Complete Your Tax Return
                        </span>
                        <div className="price-breakdown-subtitle">
                            Please review and proceed to the next step.
                        </div>
                        <div>
                            <img src={hmrcBadge} alt="HMRC badge" />
                        </div>
                    </div>
                </div>
                <div>
                    <Typography>
                        {isLoadingOrderToken ? (
                            <Spinner isLoading={true} />
                        ) : (
                            <PriceTables
                                pricingCategory={pricingCategory}
                                discountedAmount={amount}
                                onPayNow={onPayNowClick}
                                isOnPaywall={true}
                                setAccountantReviewRequested={setAccountantReviewRequested}
                                setAmount={setAmount}
                            />
                        )}
                    </Typography>
                </div>
            </div>
        ) : (
            <div className="price-breakdown-outer-div-mobile common-font-style">
                <div className="why-you-need-to-pay-container-mobile">
                    <span className="price-breakdown-title">
                            Complete Your Tax Return
                    </span>
                    <div className="price-breakdown-subtitle">
                            You’ve done the hard part. Now submit your tax return with confidence.
                    </div>
                    <div className="price-breakdown-subtitle-bold">
                            Payment Breakdown
                    </div>
                    <div className="mb10">
                        <div className="price-breakdown-row">
                            <span className="price-breakdown-label">  {selectedKey !== TableCategory.AccountantReviewedReturn
                                ? employmentTypeCard?.category
                                : selectedKey === TableCategory.AccountantReviewedReturn
                                    ? accountantCard?.category
                                    : ""}</span>
                            <span className="price-breakdown-value">£{getNetPriceExcludingVAT()}</span>
                        </div>
                        <div className="price-breakdown-row">
                            <span className="price-breakdown-label">VAT</span>
                            <span className="price-breakdown-value">£{getVATFromDiscountedGross()}</span>
                        </div>
                        {Number(getDiscount()) > 0 && (
                            <div className="price-breakdown-row brand-light-color">
                                <span className="price-breakdown-label pl20">{" "} Discount</span>
                                <span className="price-breakdown-value">-£{Number(getDiscount()).toFixed(2)}</span>
                            </div>
                        )}
                        <div className="price-breakdown-row">
                            <span className="price-breakdown-label bold">Total cost</span>
                            <span className="price-breakdown-value bold">
                                    £{getTotalCost()}</span>
                        </div>
                    </div>
                    <div className="mb10">
                        <DiscountBox setAmount={setAmount} setOldAmount={setOldAmount} amount={amount} documentId={documentId}/>
                    </div>
                </div>
                <div className="mb10">
                    <Typography>
                        {isLoadingOrderToken ? (
                            <Spinner isLoading={true} />
                        ) : (
                            <div>
                                <><div className="price-breakdown-subtitle-bold">Payment Plans</div>
                                    <PriceCard
                                        isSelected={selectedKey === employmentTypeCard?.category}
                                        onSelect={() => {
                                            setSelectedKey(employmentTypeCard?.category ?? null);
                                            setAccountantReviewRequested?.(false);
                                            onPayNowClick?.(amount);
                                        } }
                                        category={employmentTypeCard?.category ?? ""}
                                        popular="Most Popular"
                                        originalPrice={employmentTypeCard?.originalPrice ?? "0.00"}
                                        description="/ one time payment"
                                        rows={[
                                            { text: "Your tax return filed directly to HMRC", showCheck: true },
                                            { text: "Full breakdown of your tax calculation for your review and approval", showCheck: true },
                                        ]}
                                        setAmount={setAmount}
                                        amount={amount}
                                        className={selectedKey !== TableCategory.AccountantReviewedReturn
                                            ? "price-tables__card-purple-mobile"
                                            : "price-tables__card-mobile"}
                                        isActive={selectedKey !== TableCategory.AccountantReviewedReturn}
                                        isPaymentMode={true} /><PriceCard
                                        isSelected={selectedKey === TableCategory.AccountantReviewedReturn}
                                        onSelect={() => {
                                            setSelectedKey(TableCategory.AccountantReviewedReturn);
                                            setAccountantReviewRequested?.(true);
                                            onPayNowClick?.(amount);
                                        } }
                                        category={TableCategory.AccountantReviewedReturn}
                                        originalPrice={accountantCard?.originalPrice ?? amount.toFixed(2)}
                                        description="/ one time payment"
                                        rows={[
                                            {
                                                text: "A certified accountant works on your tax return to help claim allowable expenses and other reliefs",
                                                showCheck: true,
                                            },
                                            {
                                                text: "Full breakdown of your tax calculation for your review and approval",
                                                showCheck: true,
                                            },
                                            {
                                                text: "We file your return with HMRC",
                                                showCheck: true,
                                            },
                                        ]}
                                        setAmount={setAmount}
                                        amount={amount}
                                        className={selectedKey === TableCategory.AccountantReviewedReturn
                                            ? "price-tables__card-purple-mobile"
                                            : "price-tables__card-mobile"}
                                        isActive={selectedKey === TableCategory.AccountantReviewedReturn}
                                        isPaymentMode={true} />
                                </>
                            </div>
                        )}
                    </Typography>
                </div>
            </div>
        )
    );
};
